import { Suspense, lazy, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectWebPushEnabled, selectMyNotificationsEnabled } from '../brand/ducks/selectors.js';
import {
	ServerNotificationListener,
	MyNotificationSlider,
	MyNotificationAdditionalDataPopup,
	MyNotificationBluebar
} from '~/components/account/async.js';
import Notifications from '../notifications/notifications-async.js';
import FloatingSupportButton from '../support/floating-support-button.js';
import AdaMetafieldsController from '~/components/support/ada-metafields-controller';
import SystemMessage from '~/components/account/system-message';
import { setVIPChatTagCVValue, showSupportDialogBox } from '~/components/support/ducks';
import selectSupport from '~/components/support/ducks/selectors';
import { insertInitialMetaForFrame } from '@comeon/analytics';
import { ANALYTICS_EVENT_SITE } from '@comeon/analytics/analytics-events';
import useUIContext from '~/ui/use-ui-context';
import NotificationsQueueContainer from '~/components/notifications/notifications-queue-container';
import { NotificationsWrapper } from '~/components/notifications/notifications.js';

// Components lazy loaded
const WebPush = lazy(() => import('../web-push/web-push.js'));

const CRMComponents = () => {
	const webPushEnabled = useSelector(selectWebPushEnabled);
	const myNotificationsEnabled = useSelector(selectMyNotificationsEnabled);
	const notificationBluebar = useSelector(state => state.myNotifications.bluebarNotification);
	const support = useSelector(selectSupport);
	const dispatch = useDispatch();
	const { hasStaticHeader, withinSidebar } = useUIContext();

	useEffect(() => {
		insertInitialMetaForFrame(ANALYTICS_EVENT_SITE, dispatch, eventInfo => {
			const parsedData = JSON.parse(eventInfo.replace(/'/g, '"'));
			if (parsedData.pixel_cv) {
				dispatch(setVIPChatTagCVValue(parseInt(parsedData.pixel_cv, 10)));
			}
		});

		const searchParams = new URLSearchParams(window.location.search);
		if (searchParams.get('cx') === 'true') {
			dispatch(showSupportDialogBox(true));
		}

		if (support?.ada?.staticImport) {
			// this is to import ada on load if static importing is enabled.
			dispatch(
				showSupportDialogBox(false, () => {
					const adaElement = document.querySelector('#ada-button-frame');
					adaElement.style.display = 'none';
				})
			);
		}
	}, [dispatch, support?.ada?.staticImport]);

	return (
		<Suspense fallback={<div className="spinner" />}>
			{webPushEnabled && <WebPush />}
			{myNotificationsEnabled && (
				<>
					<ServerNotificationListener />
					<MyNotificationAdditionalDataPopup />
					<MyNotificationSlider />
					{notificationBluebar && <MyNotificationBluebar />}
				</>
			)}
			<FloatingSupportButton />
			<AdaMetafieldsController />
			<NotificationsWrapper
				withinSidebar={withinSidebar}
				hasStaticHeader={hasStaticHeader}
				className="notifications-wrapper"
			>
				<Notifications />
				<NotificationsQueueContainer />
			</NotificationsWrapper>
			<SystemMessage />
		</Suspense>
	);
};

export default CRMComponents;
